import LegalPage from "@/pages/LegalPage";
import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";

// Eager-loaded — needed on first paint or auth flow.
import AuthScreen from "@/pages/AuthScreen";
import AuthCallback from "@/pages/AuthCallback";
import IncomingCallListener from "@/components/IncomingCallListener";
import FeedPage from "@/pages/FeedPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MagicLinkPage from "@/pages/MagicLinkPage";
import MagicLinkConsumePage from "@/pages/MagicLinkConsumePage";
import OnboardingPage from "@/pages/OnboardingPage";
import ChooseUsernamePage from "@/pages/ChooseUsernamePage";

// Lazy-loaded — split into separate chunks to shrink the initial bundle.
const ProfilePage = React.lazy(() => import("@/pages/ProfilePage"));
const SearchPage = React.lazy(() => import("@/pages/SearchPage"));
const CreatePostPage = React.lazy(() => import("@/pages/CreatePostPage"));
const PostDetailPage = React.lazy(() => import("@/pages/PostDetailPage"));
const DiscoverPage = React.lazy(() => import("@/pages/DiscoverPage"));
const FollowRequestsPage = React.lazy(() => import("@/pages/FollowRequestsPage"));
const NotificationsPage = React.lazy(() => import("@/pages/NotificationsPage"));
const DmsListPage = React.lazy(() => import("@/pages/DmsListPage"));
const NewGroupPage = React.lazy(() => import("@/pages/NewGroupPage"));
const ConversationPage = React.lazy(() => import("@/pages/ConversationPage"));
const VerifyIdentityPage = React.lazy(() => import("@/pages/VerifyIdentityPage"));
const CallPage = React.lazy(() => import("@/pages/CallPage"));
const SettingsPage = React.lazy(() => import("@/pages/SettingsPage"));
const StoryComposerPage = React.lazy(() => import("@/pages/StoryComposerPage"));
const RoomsListPage = React.lazy(() => import("@/pages/RoomsListPage"));
const RoomPage = React.lazy(() => import("@/pages/RoomPage"));
const LiveBroadcastPage = React.lazy(() => import("@/pages/LiveBroadcastPage"));
const LiveViewerPage = React.lazy(() => import("@/pages/LiveViewerPage"));
const ReelsPage = React.lazy(() => import("@/pages/ReelsPage"));
const FollowersPage = React.lazy(() => import("@/pages/FollowersPage"));
const HashtagPage = React.lazy(() => import("@/pages/HashtagPage"));
const BillingPage = React.lazy(() => import("@/pages/BillingPage"));

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="page-loading">
      <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

// Warm up the chunks most likely to be visited right after the AuthScreen.
// Runs when the browser is idle so it never competes with first paint.
function preloadHotRoutes() {
  if (typeof window === "undefined") return;
  const warm = () => {
    import("@/pages/CreatePostPage");
    import("@/pages/ProfilePage");
    import("@/pages/ReelsPage");
    import("@/pages/DiscoverPage");
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 4000 });
  } else {
    setTimeout(warm, 2000);
  }
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  if (user && !user.username) return <Navigate to="/choose-username" replace />;
  if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
  if (user && user.onboarded && !user.verified) return <Navigate to="/verify" replace />;
  return children;
}

function Protected({ children }) {
  return <ProtectedRoute><OnboardingGate>{children}</OnboardingGate></ProtectedRoute>;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Capacitor native bootstrap: hide splash, set status bar, wire deep links + back button.
  React.useEffect(() => {
    import("@/lib/native").then(({ initNative }) => initNative(navigate)).catch(() => {});
    // Warm hot routes once the main bundle has settled.
    preloadHotRoutes();
  }, [navigate]);
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/" element={<AuthScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/magic-link" element={<MagicLinkPage />} />
        <Route path="/auth/magic" element={<MagicLinkConsumePage />} />
        <Route path="/choose-username" element={<ProtectedRoute><ChooseUsernamePage /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/verify" element={<ProtectedRoute><VerifyIdentityPage /></ProtectedRoute>} />
        <Route path="/feed" element={<Protected><FeedPage /></Protected>} />
        <Route path="/discover" element={<Protected><DiscoverPage /></Protected>} />
        <Route path="/search" element={<Protected><SearchPage /></Protected>} />
        <Route path="/create" element={<Protected><CreatePostPage /></Protected>} />
        <Route path="/me" element={<Protected><ProfilePage selfMode /></Protected>} />
        <Route path="/u/:userId" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/p/:postId" element={<Protected><PostDetailPage /></Protected>} />
        <Route path="/h/:tag" element={<Protected><HashtagPage /></Protected>} />
        <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
        <Route path="/billing/return" element={<Protected><BillingPage /></Protected>} />
        <Route path="/follow-requests" element={<Protected><FollowRequestsPage /></Protected>} />
        <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
        <Route path="/dms" element={<Protected><DmsListPage /></Protected>} />
        <Route path="/dms/new-group" element={<Protected><NewGroupPage /></Protected>} />
        <Route path="/dms/:convId" element={<Protected><ConversationPage /></Protected>} />
        <Route path="/call/:convId" element={<Protected><CallPage /></Protected>} />
        <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
        <Route path="/stories/create" element={<Protected><StoryComposerPage /></Protected>} />
        <Route path="/rooms" element={<Protected><RoomsListPage /></Protected>} />
        <Route path="/rooms/:roomId" element={<Protected><RoomPage /></Protected>} />
        <Route path="/live/new" element={<Protected><LiveBroadcastPage /></Protected>} />
        <Route path="/live/:roomId/broadcast" element={<Protected><LiveBroadcastPage /></Protected>} />
        <Route path="/live/:roomId" element={<Protected><LiveViewerPage /></Protected>} />
        <Route path="/reels" element={<Protected><ReelsPage /></Protected>} />
        <Route path="/reels/:postId" element={<Protected><ReelsPage /></Protected>} />
        <Route path="/users/:userId/followers" element={<Protected><FollowersPage kind="followers" /></Protected>} />
        <Route path="/users/:userId/following" element={<Protected><FollowersPage kind="following" /></Protected>} />
      </Routes>
      {/* Global "incoming call" modal — mounted once, listens regardless of route. */}
      <IncomingCallListener enabled={!!user} />
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App bg-background text-foreground min-h-screen">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster theme="dark" position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
